import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Same generic string the backend returns — kept in one place so every
// failure mode shows identical copy in the UI.
const GENERIC_LOGIN_ERROR = 'Invalid email or password.';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Client-side pre-flight validation. This is defense-in-depth only —
 * the backend enforces the same rules via Pydantic + rate limiting.
 * We validate here purely to give instant feedback and skip a needless
 * roundtrip when the input is obviously malformed.
 */
function validateLoginForm(email, password) {
  const trimmedEmail = (email || '').trim();
  if (!trimmedEmail) return 'Enter your email.';
  if (trimmedEmail.length > 254) return 'Email is too long.';
  if (!EMAIL_RE.test(trimmedEmail)) return 'Enter a valid email address.';
  if (!password) return 'Enter your password.';
  if (password.length > 128) return 'Password is too long (max 128 characters).';
  return null;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Client-side validation guard.
    const validationError = validateLoginForm(email, password);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setLoading(true);
    try {
      // Trim + lowercase locally too so the rate-limit key we hit matches
      // exactly what the backend records.
      await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const status = error?.response?.status;
      const backendDetail = error?.response?.data?.detail;

      if (status === 429) {
        // Backend lockout — show the specific message with retry hint.
        // Detail already reads "Too many failed attempts. Try again in Xm."
        const retryMsg =
          typeof backendDetail === 'string'
            ? backendDetail
            : 'Too many failed attempts. Please try again shortly.';
        setFormError(retryMsg);
        toast.error(retryMsg);
      } else if (status === 401) {
        // Generic error — do NOT surface backend detail even if it differs;
        // we want IDENTICAL UX for every wrong-credential case so the UI
        // can't be used to enumerate accounts either.
        setFormError(GENERIC_LOGIN_ERROR);
        toast.error(GENERIC_LOGIN_ERROR);
      } else if (status === 422) {
        // Pydantic validation failure — shouldn't happen if client validation
        // catches it, but surface a friendly generic hint.
        setFormError('Check your email and password format and try again.');
      } else {
        // Network / 5xx — DO NOT reveal that we couldn't reach the server as
        // a login-specific error; keep it generic to avoid enumeration hints.
        const msg = 'Sign-in unavailable right now. Please try again shortly.';
        setFormError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background luxury-mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img src="/wemillionaires-logo.png" alt="We Masterminds" className="h-16 w-auto" />
            <h1 className="text-3xl font-playfair font-bold text-foreground tracking-wide">WE MASTERMINDS</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Sign in to your command center</p>
        </div>

        <div className="wm-card p-8" data-testid="login-form">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div>
              <Label htmlFor="email" className="wm-label">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                maxLength={254}
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (formError) setFormError(null); }}
                required
                aria-invalid={!!formError}
                aria-describedby={formError ? 'login-error' : undefined}
                className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 focus:border-gold focus:ring-1 focus:ring-gold/30"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="wm-label">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                maxLength={128}
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (formError) setFormError(null); }}
                required
                aria-invalid={!!formError}
                aria-describedby={formError ? 'login-error' : undefined}
                className="mt-2 bg-[#0a0a12] border-white/10 text-foreground rounded-md h-12 focus:border-gold focus:ring-1 focus:ring-gold/30"
                data-testid="password-input"
              />
            </div>

            {formError && (
              <div
                id="login-error"
                role="alert"
                aria-live="polite"
                className="p-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-200 text-sm"
                data-testid="login-error"
              >
                {formError}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gold-gradient text-black hover:shadow-gold-glow font-semibold uppercase tracking-widest rounded-md"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Enter Platform'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-muted-foreground hover:text-gold transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-gold hover:text-gold/80 transition-colors font-medium" data-testid="register-link">
                Request Access
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
